import { useState, useEffect, type CSSProperties } from 'react';

interface UserAvatarProps {
  src?: string | null;
  name?: string | null;
  email?: string | null;
  label: string;
  size?: number;
  className?: string;
  style?: CSSProperties;
}

function initialsFor(name?: string | null, email?: string | null): string {
  const source = (name || email?.split('@')[0] || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toLocaleUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toLocaleUpperCase();
}

export default function UserAvatar({
  src,
  name,
  email,
  label,
  size = 32,
  className,
  style,
}: UserAvatarProps) {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [src]);

  const sharedStyle: CSSProperties = {
    width: size,
    height: size,
    flex: `0 0 ${size}px`,
    borderRadius: '50%',
    ...style,
  };

  if (src && !imgError) {
    return (
      <img
        className={className}
        src={src}
        alt={label}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setImgError(true)}
        style={{ ...sharedStyle, objectFit: 'cover' }}
      />
    );
  }

  return (
    <span
      className={`user-avatar-fallback${className ? ` ${className}` : ''}`}
      role="img"
      aria-label={label}
      title={name || email || label}
      style={sharedStyle}
    >
      {initialsFor(name, email)}
    </span>
  );
}
